'use strict'
import React, { useCallback, useMemo } from 'react'
import { formatNumberValue } from '../../../../common/utils/NumberUtils'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import {
  fteActualWorkingHoursCalcMethodType,
  securitiesEmployeeType as securitiesEmployeeTypeConst
} from '../../../../_constants/Code'
import CustomTooltip from '../../../../_components/CustomTooltip'
import { useEmployeeCountDispatch, useEmployeeCountSelector } from '../extra/store'
import { hasErrorClass } from '../extra/common'
import { ShowErrors } from './ShowErrors'
import '../extra/types'
import { shallowEqual } from 'react-redux'

/*==================================
 定数定義
 ==================================*/
// 有報従業員区分の選択肢
const secEmpTypeOptions = Object.values(securitiesEmployeeTypeConst).map((item) => ({
  label: item.name,
  value: item.name
}))

/*==================================
 定数定義
 ==================================*/
// FTE実労働時間集計区分選択肢
const actualWorkingHoursCalcMethodOptions = Object.values(fteActualWorkingHoursCalcMethodType).map(({ name }) => ({
  label: name,
  value: name
}))

/*==================================
 カスタムフック
 ==================================*/
/**
 * 雇用区分設定に関するロジックを管理するカスタムフック。
 *
 * @returns {{
 *   employmentTypeSetting: EmploymentTypeSetting
 *   fiscalYearErrors: Errors
 *   handleChangeSecuritiesEmployee: (employmentType: string) => (event: { target: { value: string } }) => void
 *   handleChangeFteEstimatedFactor: (employmentType: string) => (e: React.ChangeEvent<HTMLInputElement>) => void
 *   handleBlurFteEstimatedFactor: (employmentType: string) => (e: React.FocusEvent<HTMLInputElement>) => void
 * }}
 */
const useEmploymentTypeSettingLogic = () => {
  // 状態取得
  const dispatch = useEmployeeCountDispatch()
  const { employmentTypeSetting, fiscalYearErrors } = useEmployeeCountSelector((state) => {
    const selectedYear = state.employeeCountInfo.selectedFiscalYear
    const fiscalYearSettings = state.employeeCountInfo.fiscalYearSettings[selectedYear]

    return {
      employmentTypeSetting: fiscalYearSettings?.employmentTypeSetting ?? [],
      fiscalYearErrors: fiscalYearSettings?.errors?.client ?? {}
    }
  }, shallowEqual)

  /**
   * 有報従業員区分リスト変更処理（変更アクションをdispatch）
   *
   * @param {string} employmentType - 変更対象の雇用区分（キー）
   * @returns {(event: { target: { value: string } }) => void} - イベントオブジェクト => 有報従業員区分（選択値）
   */
  const handleChangeSecuritiesEmployee = useCallback(
    (employmentType) => (e) => {
      const securitiesEmployeeType = e?.target?.value ?? ''
      dispatch('CHANGE_SECURITIES_EMP_TYPE', { employmentType, securitiesEmployeeType })
    },
    [dispatch]
  )

  /**
   * FTE概算係数変更処理（変更アクションをdispatch）=> onChange
   *
   * @param {string} employmentType - 変更対象の雇用区分（キー）
   * @returns {(e: React.ChangeEvent<HTMLInputElement>) => void} - イベント（FTE概算係数（入力値））
   */
  const handleChangeFteEstimatedFactor = useCallback(
    (employmentType) => (e) => {
      dispatch('CHANGE_FTE_ESTIMATED_FACTOR', { employmentType, fteEstimatedFactor: e.target.value })
    },
    [dispatch]
  )

  /**
   * FTE概算係数変更処理（変更アクションをdispatch）=> onBlur
   *
   * @param {string} employmentType - 変更対象の雇用区分（キー）
   * @returns {(e: React.FocusEvent<HTMLInputElement>) => void} - イベント（FTE概算係数（入力値））
   */
  const handleBlurFteEstimatedFactor = useCallback(
    (employmentType) => (e) => {
      // トリム処理
      const trimmedValue = e.target.value.trim()
      // 数値チェック、フォーマット変換
      const formattedValue = formatNumberValue(trimmedValue, 2, false)

      // フォーマットされた値をdispatch
      dispatch('CHANGE_FTE_ESTIMATED_FACTOR', {
        employmentType,
        fteEstimatedFactor: formattedValue
      })
    },
    [dispatch]
  )

  /**
   * @function
   * @param {string} fteActualWorkingHours - 変更対象の雇用区分（キー）
   * @returns {(event: { target: { value: string } }) => void} - イベントオブジェクト => 有報従業員区分（選択値）
   */
  const handleFteActualWorkingHoursCalcMethod = useCallback(
    (employmentType) => (e) => {
      const fteActualWorkingHoursCalcMethod = e?.target?.value ?? ''
      dispatch('CHANGE_FTE_ACTUAL_WORKING_HOURS_CALC_METHOD', { employmentType, fteActualWorkingHoursCalcMethod })
    },
    [dispatch]
  )

  return {
    employmentTypeSetting,
    fiscalYearErrors,
    handleChangeSecuritiesEmployee,
    handleChangeFteEstimatedFactor,
    handleBlurFteEstimatedFactor,
    handleFteActualWorkingHoursCalcMethod
  }
}

/*==================================
    コンポーネント（JSX）
  ===================================*/
/**
 * 雇用区分設定のテーブルを表示するコンポーネント
 *
 * このコンポーネントは、雇用区分設定テーブルと関連するエラー表示をレンダリング
 *
 * @returns {React.ReactElement} - 雇用区分設定テーブル
 * @component
 */
const EmploymentTypeSettingTable = React.memo(({ employmentTypeSetting, clientErrors, testId = '' }) => {
  const {
    handleChangeSecuritiesEmployee,
    handleChangeFteEstimatedFactor,
    handleBlurFteEstimatedFactor,
    handleFteActualWorkingHoursCalcMethod
  } = useEmploymentTypeSettingLogic()

  const SelectClassList = ['w-full']
  const SelectClasses = SelectClassList.join(' ')

  if (!employmentTypeSetting || employmentTypeSetting.length === 0) {
    return (
      <div className="text-center my-10">
        <span className="text-h5 red--text">雇用区分が未登録です。</span>
      </div>
    )
  }

  return (
    <div className="setting-table min-w-[1000px] max-w-[80%]" data-testid={testId}>
      <table className="base-table table-fixed w-full">
        <colgroup>
          <col className="w-[40%]" />
          <col className="w-[20%]" />
          <col className="w-[20%]" />
          <col className="w-[20%]" />
        </colgroup>
        <thead>
          <tr>
            <th>雇用区分</th>
            <th>有報従業員区分</th>
            <th>FTE雇用区分概算係数</th>
            <th>FTE実労働時間集計区分</th>
          </tr>
        </thead>
        <tbody className="text-caption-2 !text-base font-medium">
          {employmentTypeSetting.map((empType, idx) => {
            return (
              <tr key={idx} data-testid={'test'}>
                <td>
                  <CustomTooltip title={empType.employmentType} useParentWidth={true}>
                    {(ref) => (
                      <div ref={ref} className="truncate font-normal">
                        <span>{empType.employmentType}</span>
                      </div>
                    )}
                  </CustomTooltip>
                </td>
                <td className="form-control !py-2">
                  <ChrofyCustomSelect
                    id="securities-employee-type"
                    name={`securitiesEmployeeType_${empType.employmentType}`}
                    value={empType.securitiesEmployeeType}
                    onChange={handleChangeSecuritiesEmployee(empType.employmentType)}
                    className={`${SelectClasses}`}
                    options={secEmpTypeOptions}
                    disabled={false}
                    isError={hasErrorClass(`securitiesEmployeeType_${empType.employmentType}`, clientErrors)}
                    hasNoSelect={false}
                  />
                </td>
                <td className="form-control !py-2">
                  <input
                    id="fte-estimated-factor"
                    name={`fteEstimatedFactor_${empType.employmentType}`}
                    placeholder="数値を入力"
                    type="text"
                    className={
                      `text-input text-[14px] format-digit rounded-xl w-full min-h-[46px] ` +
                      ` focus:ring-2 focus:ring-gray-900 focus:ring-offset-0` +
                      ` ${hasErrorClass(`fteEstimatedFactor_${empType.employmentType}`, clientErrors) ? 'is-error' : ''}`
                    }
                    value={empType.fteEstimatedFactor ?? ''}
                    onChange={handleChangeFteEstimatedFactor(empType.employmentType)}
                    onBlur={handleBlurFteEstimatedFactor(empType.employmentType)}
                  />
                </td>
                <td className="form-control !py-2">
                  <ChrofyCustomSelect
                    id="fte-actual-working-hours"
                    name={`fteActualWorkingHoursCalcMethod_${empType.employmentType}`}
                    value={empType.fteActualWorkingHoursCalcMethod}
                    onChange={handleFteActualWorkingHoursCalcMethod(empType.employmentType)}
                    className={`${SelectClasses}`}
                    options={actualWorkingHoursCalcMethodOptions}
                    disabled={false}
                    isError={hasErrorClass(`fteActualWorkingHoursCalcMethod_${empType.employmentType}`, clientErrors)}
                    hasNoSelect={false}
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
})

/**
 * 雇用区分に関する設定セクション全体を管理するコンポーネント
 *
 * このコンポーネントは、雇用区分設定テーブルと関連するエラー表示をレンダリング
 *
 * @returns {React.ReactElement} - 雇用区分設定のUI要素を含むReactエレメント
 * @component
 */
const EmploymentTypeSetting = () => {
  const { employmentTypeSetting, fiscalYearErrors } = useEmploymentTypeSettingLogic()

  return (
    <>
      <div className="text-body-2-bold grow mb-2 ">■雇用区分に関する設定</div>
      <div className="mb-6">
        <div>雇用区分ごとに以下の設定をしてください。</div>
        <div className="flex mt-2 gap-x-4">
          <div className="w-60 shrink-0">① 有報従業員区分</div>
          <div>
            有価証券報告書の従業員数（従業員の状況）の集計区分を設定します。対象外とした雇用区分は従業員数としてカウントされません。
          </div>
        </div>
        <div className="flex mt-2 gap-x-4">
          <div className="w-60 shrink-0">② FTE雇用区分概算係数</div>
          <div>
            臨時従業員の計算方法をFTE（雇用区分概算）を選択した場合に、設定します。
            <br />
            雇用区分概算係数（1人あたりの正規従業員換算比率）を乗じた「換算人員数」として集計する場合には、雇用区分ごとに雇用区分概算係数を設定してください。
          </div>
        </div>
        <div className="flex mt-2 gap-x-4">
          <div className="w-60 shrink-0">③ FTE実労働時間集計区分</div>
          <div>
            臨時従業員の計算方法をFTE（実労働時間概算）を選択した場合に、設定します。
            <br />
            実労働時間で集計しない雇用区分については、FTE雇用区分概算係数を選択したうえで、FTE雇用区分概算係数を設定してください。
            <br />
            FTE雇用区分概算係数が設定されていない場合には、ヘッドカウントで集計されます。
          </div>
        </div>
      </div>
      <EmploymentTypeSettingTable employmentTypeSetting={employmentTypeSetting} clientErrors={fiscalYearErrors} />
      <ShowErrors clientErrors={fiscalYearErrors} fieldName="employmentTypeSetting" />
    </>
  )
}

export default EmploymentTypeSetting
